import React from 'react';
import { css } from '@emotion/react';
import { Capacitor } from '@capacitor/core';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { useTranslation } from 'react-i18next';
import { spacing, fontStyles } from '../../styles';
import Modal from '../ModalPopUp/Modal';
import Button from '../Button';

interface NotificationBlockedModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationBlockedModal: React.FC<NotificationBlockedModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const openNotificationSettings = async () => {
    // Check if we're on web platform
    if (Capacitor.getPlatform() === 'web') {
      // On web, we can't open device settings, so show instructions
      alert(t('settings.notificationBrowserInstructions'));
      onClose();
      return;
    }

    try {
      await NativeSettings.open({
        optionAndroid: AndroidSettings.AppNotification,
        optionIOS: IOSSettings.AppNotification,
      });
      onClose();
    } catch (error) {
      console.error('Error opening settings:', error);
      // Fallback: try to open general settings
      try {
        await NativeSettings.open({
          optionAndroid: AndroidSettings.ApplicationDetails,
          optionIOS: IOSSettings.App,
        });
        onClose();
      } catch (fallbackError) {
        console.error('Error opening general settings:', fallbackError);
        onClose();
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('notifications.blocked')} isDismissable={true} maxWidth="400px">
      <div
        css={css`
          padding: ${spacing.regular};
          text-align: center;
        `}
      >
        <div
          css={css`
            font-size: 48px;
            margin-bottom: ${spacing.regular};
          `}
        >
          🔕
        </div>

        <p
          css={css`
            ${fontStyles.dog};
            color: var(--text-color);
            margin-bottom: ${spacing.regular};
            line-height: 1.5;
          `}
        >
          {t('notifications.enablePermissions')}
        </p>

        <p
          css={css`
            ${fontStyles.dog};
            color: var(--secondary-text);
            margin-bottom: ${spacing.deka};
            font-size: 14px;
            line-height: 1.4;
          `}
        >
          {t('notifications.goToSettingsDescription')}
        </p>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${spacing.deci};
          `}
        >
          <Button
            onClick={openNotificationSettings}
            css={css`
              width: 100%;
              padding: ${spacing.deci} ${spacing.regular};
            `}
          >
            {t('notifications.goToSettings')}
          </Button>

          <Button
            onClick={onClose}
            variant="secondary"
            css={css`
              width: 100%;
              padding: ${spacing.deci} ${spacing.regular};
              background: transparent;
              color: var(--secondary-text);
              border: 1px solid var(--border-color);

              &:hover {
                background: var(--hover-background);
              }
            `}
          >
            {t('notifications.cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default NotificationBlockedModal;
